import React, { useState, useContext } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Paper
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import { useAuthenticator } from '@aws-amplify/ui-react';
import SlugAdmin from '../administration/SlugAdmin';
import UserAdmin from '../administration/UserAdmin';
import UnifiedSettingsDialog from './dialogs/UnifiedSettingsDialog';
import SearchAppBar from './search/SearchAppBar';
import FilterDrawer from './search/FilterDrawer';
import SlugSearchResults from './people/SlugSearchResults';
import { useSettings } from '../contexts/settingsContext';
import { useSearch } from '../contexts/searchContext';
import { CognitoUserContext } from '../contexts/cognitoUserContext';
import { HistoryDrawerProvider } from '../contexts/historyDrawerContext';
import { useSearchPeople } from './search/useSearchPeople';
import {
  setLocalStorageEnumMap,
  clearLocalStorageEnumMap,
} from '../services/user/browserService';

/**
 * AdminApp - Administrator-only interface
 * Provides access to bulk data management operations + search for editing characteristics
 */
export default function AdminApp() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Admin Tools, 1 = User Admin, 2 = Search
  const [drawerOpen, setDrawerOpen] = useState(false);
  const settings = useSettings();

  const { signOut } = useAuthenticator();
  const { cognitoUser } = useContext(CognitoUserContext);

  // Use unified search context instead of 3 separate contexts
  const {
    datasetMap,
    selectedDataset,
    enumMap,
    setEnumMap,
    searchString,
    setFilteredPeople,
    setMatchSource
  } = useSearch();

  const { searchPeople } = useSearchPeople(setFilteredPeople, setMatchSource);

  const myarr = Array.from(enumMap);

  const handleSignOut = () => signOut();

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    const tempMap = new Map(enumMap);
    tempMap.set('Dataset', selectedDataset);
    setLocalStorageEnumMap(tempMap);
    searchPeople(tempMap, searchString);
    setDrawerOpen(false);
  };

  const handleDrawerQuit = () => {
    setDrawerOpen(false);
  };

  const handleDrawerClear = () => {
    const clearMap = new Map();
    datasetMap.set(selectedDataset, clearMap);
    setEnumMap(clearMap);
    clearLocalStorageEnumMap();
  };

  return (
    <HistoryDrawerProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {/* App Bar */}
        <AppBar position="static" sx={{ bgcolor: '#2d3e50' }}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Administration Panel
            </Typography>

            <IconButton
              color={activeTab === 0 ? "secondary" : "inherit"}
              onClick={() => setActiveTab(0)}
              title="Admin Tools"
            >
              <AdminPanelSettingsIcon />
            </IconButton>

            <IconButton
              color={activeTab === 1 ? "secondary" : "inherit"}
              onClick={() => setActiveTab(1)}
              title="User Admin"
            >
              <PeopleIcon />
            </IconButton>

            <IconButton
              color={activeTab === 2 ? "secondary" : "inherit"}
              onClick={() => setActiveTab(2)}
              title="Search & Edit"
            >
              <SearchIcon />
            </IconButton>

            <IconButton
              color="inherit"
              onClick={() => setSettingsOpen(true)}
              title="Settings"
            >
              <SettingsIcon />
            </IconButton>

            <IconButton
              color="inherit"
              onClick={handleSignOut}
              title="Sign Out"
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ mt: 2, mb: 2, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {activeTab === 0 && (
            <Paper sx={{ p: 3 }}>
              <SlugAdmin />
            </Paper>
          )}

          {activeTab === 1 && (
            <Paper sx={{ p: 2, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <UserAdmin />
            </Paper>
          )}

          {activeTab === 2 && (
            <Box>
              <SearchAppBar
                drawerOpen={drawerOpen}
                onHistoryOpen={() => {}} // No history for admins
                onCameraOpen={() => {}} // No camera for admins
                onStealthOpen={() => {}} // No stealth for admins
                onDrawerOpen={handleDrawerOpen}
                onSettingsOpen={() => setSettingsOpen(true)}
                onAdminOpen={() => {}} // Already in admin mode
                cognitoUser={cognitoUser}
                stealthMode={false}
                myarr={myarr}
                isAdminMode={true}
              />

              <FilterDrawer
                open={drawerOpen}
                onClose={handleDrawerQuit}
                onApply={handleDrawerClose}
                onClear={handleDrawerClear}
                enumMap={enumMap}
              />

              <Box sx={{ mt: 2 }}>
                <SlugSearchResults />
              </Box>
            </Box>
          )}
        </Container>

      {/* Settings Dialog */}
      <UnifiedSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        // History settings
        deleteConfirmation={settings.deleteConfirmation}
        onDeleteConfirmationChange={settings.handleDeleteConfirmationChange}
        autoEnableVoice={settings.autoEnableVoice}
        onAutoEnableVoiceChange={settings.setAutoEnableVoice}
        stealthMode={settings.stealthMode}
        onStealthModeChange={settings.setStealthMode}
        // Camera settings
        removeBg={settings.removeBg}
        setRemoveBg={settings.setRemoveBg}
        sliderValue={settings.sliderValue}
        setSliderValue={settings.setSliderValue}
        maxFacesValue={settings.maxFacesValue}
        setMaxFacesValue={settings.setMaxFacesValue}
        showBoundingBoxes={settings.showBoundingBoxes}
        setShowBoundingBoxes={settings.setShowBoundingBoxes}
        immediateFaceMatch={settings.immediateFaceMatch}
        setImmediateFaceMatch={settings.setImmediateFaceMatch}
        updateCharacteristicsOnMatch={settings.updateCharacteristicsOnMatch}
        setUpdateCharacteristicsOnMatch={settings.setUpdateCharacteristicsOnMatch}
        compressionQuality={settings.compressionQuality}
        setCompressionQuality={settings.setCompressionQuality}
        autoAdjustCompression={settings.autoAdjustCompression}
        setAutoAdjustCompression={settings.setAutoAdjustCompression}
        autoCreateEventOnAIMatch={settings.autoCreateEventOnAIMatch}
        setAutoCreateEventOnAIMatch={settings.setAutoCreateEventOnAIMatch}
        locationHighAccuracy={settings.locationHighAccuracy}
        setLocationHighAccuracy={settings.setLocationHighAccuracy}
        locationAcquisitionTimeout={settings.locationAcquisitionTimeout}
        setLocationAcquisitionTimeout={settings.setLocationAcquisitionTimeout}
        locationCacheMaxAge={settings.locationCacheMaxAge}
        setLocationCacheMaxAge={settings.setLocationCacheMaxAge}
        aggregatorEventLimit={settings.aggregatorEventLimit}
        setAggregatorEventLimit={settings.setAggregatorEventLimit}
      />
      </Box>
    </HistoryDrawerProvider>
  );
}
